/**
 * @module fx/audioPlayer
 * Native-Audio implementation of the {@link SoundPlayer} contract.
 *
 * Preloads every game SFX as an HTMLAudioElement prototype, then clones on
 * play so overlapping sounds (rapid slashes) don't cut each other off.
 * Combo pitch-up uses `playbackRate` with `preservesPitch = false`, which is
 * the native equivalent of Howler's `rate` option — no extra dependency.
 *
 * Sound files are procedurally generated WAVs (see scripts/generate-sounds.mjs
 * and public/assets/sounds/README.md).
 */

import type { SoundPlayer } from './AudioManager.js';

/** AudioManager logical name → sound file URL. */
const SOUND_FILES: Record<string, string> = {
  slash: '/assets/sounds/slash.wav',
  splat: '/assets/sounds/fruit-hit.wav',
  bomb: '/assets/sounds/bomb.wav',
  combo: '/assets/sounds/combo.wav',
  powerup: '/assets/sounds/power-double.wav',
  ui: '/assets/sounds/ui-click.wav',
  frenzy: '/assets/sounds/frenzy-start.wav',
};

/** Playback volume for all SFX (0–1). */
const SFX_VOLUME = 0.9;

/**
 * Creates a {@link SoundPlayer} backed by native HTMLAudioElement playback.
 * Safe to construct before any user gesture — play() failures from autoplay
 * policy are swallowed (the first real gesture unlocks audio).
 */
export function createNativeAudioPlayer(): SoundPlayer {
  const prototypes = new Map<string, HTMLAudioElement>();

  for (const [name, url] of Object.entries(SOUND_FILES)) {
    const audio = new Audio(url);
    audio.preload = 'auto';
    prototypes.set(name, audio);
  }

  return {
    play(name: string, rate = 1): void {
      const proto = prototypes.get(name);
      if (!proto) return;

      const node = proto.cloneNode(true) as HTMLAudioElement;
      node.volume = SFX_VOLUME;
      node.playbackRate = rate;
      // Let pitch follow playback rate — this IS the combo pitch-up effect.
      node.preservesPitch = false;
      void node.play().catch(() => {
        // Autoplay policy blocks audio before the first user gesture — fine.
      });
    },
  };
}
